import { ApolloProvider } from '@apollo/client';
import '@material-symbols/font-500/rounded.css';
import {
  API_HOST,
  AppErrorBoundary,
  BRAND_NAME,
  ENVIRONMENT,
  LAUNCH_DARKLY_SDK_KEY,
  LoadingPage,
  sentryConfigs,
  SharedAppProviders,
} from '@pm/core';
import { Toaster } from '@pm/ui';
import * as Sentry from '@sentry/react';
import { ComponentProps, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router';
import { apolloClient } from './api/client';
import App from './apps';
import { TopbarLogo } from './layout/TopbarLogo';
import SEO from './utilities/seo';

// translations!
import './localization/i18n';

// css files
import '@pm/ui/themes/frida.css';
import '@pm/ui/themes/global.css';
import 'tailwindcss/tailwind.css';

console.log(
  'Environment variables:',
  import.meta.env.VITE_ENVIRONMENT,
  import.meta.env.VITE_GIT_PULL_REQUEST_ID,
);

const environment = {
  prod: 'prod',
  dev: 'dev',
};
const isProdEnv = import.meta.env.VITE_ENVIRONMENT === 'production';

// initialize our Sentry crash reporting tool
if (import.meta.env.PROD) {
  const sentryEnvironment = isProdEnv ? environment.prod : environment.dev;
  const networkDetailAllowUrls = [`${API_HOST}/graphql`];

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: sentryEnvironment,
    enableLogs: true,
    enableMetrics: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: sentryEnvironment === environment.prod,
        mask: ['.sensitive', '.sensitive *'],
        networkDetailAllowUrls,
      }),
      Sentry.consoleLoggingIntegration({
        levels: ['debug', 'log', 'warn', 'error'],
      }),
    ],
    replaysSessionSampleRate:
      sentryEnvironment === environment.prod ? 1.0 : 0.0,
    replaysOnErrorSampleRate: 1.0,
    tracesSampleRate: sentryEnvironment === environment.prod ? 0.5 : 1.0,
    tracePropagationTargets: [
      'https://api.talkwithfrida.com/graphql',
      'https://api-staging.talkwithfrida.com/graphql',
    ],
    sendDefaultPii: true,
    ...sentryConfigs,
  });
}

const ENV_OPTIONS: Omit<
  ComponentProps<typeof SharedAppProviders>,
  'children'
> = {
  brand: BRAND_NAME.Frida,
  apiHost: API_HOST,
  environment: ENVIRONMENT,
  launchDarklySDKKey: LAUNCH_DARKLY_SDK_KEY,
  formattingContext: 'patient',
  enableGoogleAnalyticsTracking: true,
} as const;

// render the app
createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <BrowserRouter>
      <AppErrorBoundary
        header={
          <div className="flex h-16 w-full items-center justify-center bg-white shadow-sm">
            <TopbarLogo />
          </div>
        }
        contactSupportLink="https://talkwithfrida.zendesk.com/hc/en-us/requests/new"
      >
        <HelmetProvider>
          <SEO />
          <Suspense fallback={<LoadingPage />}>
            <ApolloProvider client={apolloClient}>
              <SharedAppProviders {...ENV_OPTIONS}>
                <Toaster>
                  <App />
                </Toaster>
              </SharedAppProviders>
            </ApolloProvider>
          </Suspense>
        </HelmetProvider>
      </AppErrorBoundary>
    </BrowserRouter>
  </StrictMode>,
);
